import React, { memo, Fragment, useMemo, useState, useCallback } from 'react';
import { CalendarEventResponse } from '@business/entities/calendar';
import { CardTitle, Collapse, Col, Row, Card, CardBody } from 'reactstrap';
import { CBody } from '@modules/common/CBody';
import { toMoment } from '@core/utils/time';
import { RRule } from 'rrule';
import { TableAction, TableActions } from '@modules/common/TableActions';
export interface EventItemProps {
  event: CalendarEventResponse;
  onDelete(): void;
}
const EventItem = memo((props: EventItemProps) => {
  const { event } = props;

  const startTime = useMemo(() => toMoment(event.when.start_time), [event]);
  const endTime = useMemo(() => toMoment(event.when.end_time), [event]);
  const isSameDay = useMemo(() => startTime.isSame(endTime, 'day'), [startTime, endTime]);

  const sameMeridiem = startTime.format('a') === endTime.format('a');
  const time = useMemo(() => {
    if (isSameDay) {
      // Check if same am pm
      return `${startTime.format('dddd, MMMM D · h:mm' + (!sameMeridiem ? 'a' : ''))} － ${endTime.format('h:mma')}`;
    }

    return `${startTime.format('MMMM d, YYYY,  h:mm' + (!sameMeridiem ? 'a' : ''))} － ${endTime.format('MMM d, YYYY, h:mma')}`;
  }, [isSameDay, startTime, sameMeridiem, endTime]);

  const recurringText = useMemo(() => {
    if (!event.recurrence) return null;
    const ruleString = event.recurrence;
    if (ruleString.length) {
      const rrule = new RRule(RRule.parseString(ruleString[0]));
      return rrule.toText();
    }
    return null;
  }, [event.recurrence]);

  const MAX_LENGTH = 7;
  const moreParticipants = useMemo(() => event.participants.length > MAX_LENGTH, [event]);
  const [isOpen, setOpen] = useState(!moreParticipants);
  const yesParticipants = useMemo(() => event.participants.filter(p => p.status === 'yes'), [event]);
  const noParticipants = useMemo(() => event.participants.filter(p => p.status === 'no'), [event]);
  const waitingParticipants = useMemo(() => event.participants.filter(p => p.status !== 'yes' && p.status != 'no'), [event]);

  const getStatusIcon = useCallback((status: string) => {
    if (status === 'yes') {
      return <i className="font-size-14 mr-2 fa fa-check-circle text-success"></i>;
    } else if (status === 'no') {
      return <i className="font-size-14 mr-2 fa fa-times-circle text-danger"></i>;
    } else {
      return <i className="font-size-14 mr-2 fa fa-question-circle text-muted"></i>;
    }
  }, []);

  return (
    <Col lg={3}>
      <Card>
        <CardBody className="d-flex align-items-center">
          <div className="float-right ml-2" style={{ position: 'absolute', top: 10, right: 5 }}>
            <a
              className="text-danger"
              onClick={e => {
                props.onDelete();
                e.stopPropagation();
              }}
            >
              <i className="bx bx-trash font-size-16 text-danger"></i>
            </a>
          </div>
          <i className="bx bx-calendar" style={{ fontSize: 35, marginRight: 10 }}></i>
          <div style={{ height: 100, overflow: 'hidden', width: '100%' }}>
            <CardTitle>{event.title}</CardTitle>
            <div className="text-muted">{time}</div>
            <div className="mb-2">{recurringText}</div>

            {moreParticipants && (
              <div onClick={() => setOpen(open => !open)} style={{ cursor: 'pointer', marginBottom: 10 }}>
                <div>{event.participants.length} guests</div>
                <div className="text-muted">{yesParticipants.length} yes</div>
                <div className="text-muted">
                  {noParticipants.length > 0 && <span>{noParticipants.length} no </span>}
                  {waitingParticipants.length > 0 && <span>{waitingParticipants.length} waiting</span>}
                </div>
              </div>
            )}
            <Collapse isOpen={isOpen}>
              {event.participants.map((p, index) => (
                <div key={index} className="mb-1">
                  {getStatusIcon(p.status)}
                  <span>{p.name || p.email}</span>
                </div>
              ))}
            </Collapse>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
});

export interface DealCalendarEventsProps {
  events: CalendarEventResponse[];
  onDelete(id: string): void;
}

export const DealCalendarEvents = memo((props: DealCalendarEventsProps) => {
  const { events, onDelete } = props;
  return (
    <Fragment>
      {events.map(e => (
        <EventItem event={e} key={e.id} onDelete={() => onDelete(e.id)} />
      ))}
    </Fragment>
  );
});
